import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformsService, type ConnectMethod, type MetaPagePlatform } from '@/shared/services/platforms.service';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import type { PlatformId } from '@/shared/components/PlatformIcon';

const PLATFORMS_KEY = 'platform-statuses';

/** Fetch all platform connection statuses for the current workspace. */
export function usePlatformStatuses() {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [PLATFORMS_KEY, wsId],
    queryFn: () => platformsService.getStatuses(),
    enabled: !!wsId,
    // Connect/disconnect mutations invalidate this key, so a long stale
    // window is safe and stops every page mount from re-fetching.
    staleTime: 5 * 60 * 1000,
  });
}

/** Initiate OAuth flow — returns the authorization URL to redirect to. */
export function useConnectPlatform() {
  return useMutation({
    mutationFn: ({ platform, method }: { platform: PlatformId; method?: ConnectMethod }) =>
      platformsService.getOAuthUrl(platform, method),
  });
}

/** Disconnect a platform and invalidate the status cache. */
export function useDisconnectPlatform() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (platform: PlatformId) => platformsService.disconnect(platform),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLATFORMS_KEY] });
    },
  });
}

/** Embedded Signup configuration, read when the WhatsApp connect dialog opens. */
export function useWhatsAppSignupConfig(enabled: boolean) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: ['whatsapp-signup-config', wsId],
    queryFn: () => platformsService.getWhatsAppSignupConfig(),
    enabled: enabled && !!wsId,
    staleTime: 10 * 60 * 1000,
  });
}

/** Finish WhatsApp Embedded Signup with the code and ids Meta's dialog returned. */
export function useConnectWhatsAppEmbedded() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; wabaId: string; phoneNumberId: string; metaUserId?: string }) =>
      platformsService.connectWhatsAppEmbeddedSignup(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLATFORMS_KEY] });
    },
  });
}

/** Connect WhatsApp with a pasted System User token (admin-enabled accounts only). */
export function useConnectWhatsApp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { accessToken: string; phoneNumberId: string; wabaId: string }) =>
      platformsService.connectWhatsApp(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLATFORMS_KEY] });
    },
  });
}

/** Manually connect Telegram using a @BotFather bot token. */
export function useConnectTelegram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { botToken: string; defaultChatId?: string }) =>
      platformsService.connectTelegram(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLATFORMS_KEY] });
    },
  });
}

/** How Zetpin can be connected here (OAuth only when the server has its client). */
export function useZetpinConnectConfig(enabled = true) {
  return useQuery({
    queryKey: ['zetpin-connect-config'],
    queryFn: () => platformsService.getZetpinConnectConfig(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Connect Zetpin with a service token and the account's handle. */
export function useConnectZetpin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { serviceToken: string; accountHandle: string }) => platformsService.connectZetpin(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLATFORMS_KEY] });
    },
  });
}

/**
 * Replay webhook subscription for every active connection.
 * Used to recover platforms connected before auto-subscribe shipped,
 * or after a platform-side webhook drop.
 */
export function useResubscribeWebhooks() {
  return useMutation({
    mutationFn: () => platformsService.resubscribeWebhooks(),
  });
}

const GBP_LOCATIONS_KEY = 'google-business-locations';

/** The Google Business connection's locations; only fetched once it is connected. */
export function useGoogleBusinessLocations(enabled: boolean) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [GBP_LOCATIONS_KEY, wsId],
    queryFn: () => platformsService.getGoogleBusinessLocations(),
    enabled: enabled && !!wsId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Chooses the location Google Business posts go to. */
export function useSetGoogleBusinessLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => platformsService.setGoogleBusinessLocation(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GBP_LOCATIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PLATFORMS_KEY] });
    },
  });
}

const META_PAGES_KEY = 'meta-pages';

/** The Facebook Pages a Facebook or Instagram connection can post as, and the chosen one. */
export function useMetaPages(platform: MetaPagePlatform, enabled: boolean) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [META_PAGES_KEY, wsId, platform],
    queryFn: () => platformsService.getMetaPages(platform),
    enabled: enabled && !!wsId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Chooses the Page a Facebook or Instagram connection posts as. */
export function useSetMetaPage(platform: MetaPagePlatform) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) => platformsService.setMetaPage(platform, pageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [META_PAGES_KEY] });
      queryClient.invalidateQueries({ queryKey: [PLATFORMS_KEY] });
    },
  });
}

const LINKEDIN_ORGS_KEY = 'linkedin-organizations';

/** The Company Pages a LinkedIn Company Page connection can post as, and the chosen one. */
export function useLinkedInOrganizations(enabled: boolean) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [LINKEDIN_ORGS_KEY, wsId],
    queryFn: () => platformsService.getLinkedInOrganizations(),
    enabled: enabled && !!wsId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Chooses the Company Page a LinkedIn Company Page connection posts as. */
export function useSetLinkedInOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (organizationUrn: string) => platformsService.setLinkedInOrganization(organizationUrn),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LINKEDIN_ORGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PLATFORMS_KEY] });
    },
  });
}
